import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { store } from "Store";
import { Row, Col, Card, Form } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import { TextButton } from "Components/Button";
import { getMfaUserInfo, setupUserSmsFactor, submitMfa } from "./SetMfaAxiosFuncs";
import { STATUSES, ERROR_MESSAGES } from "Utils/constants";
import { setGlobalSuccessStatus, setGlobalErrorStatus } from 'Components/StatusHandling';
import { PageInfo } from "Components/PageInformation/PageInformation";

const SetupMfa = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginState = store.getState().loginInfo;
  const token = "Bearer " + loginState.token;
  const userEmail = loginState.username;
  const [mfaUserInfo, setMfaUserInfo] = useState(null);
  const [newPhoneRequest, setNewPhoneRequest] = useState({
    mobileNumber: null
  });
  const [mfaToken, setMfaToken] = useState("");
  const [showSendCodeMsg, setShowSendCodeMsg] = useState(false);
  const [disableSendCode, setDisableSendCode] = useState(true);
  const [disableMfaTokenField, setDisableMfaTokenField] = useState(true);
  // error messages display on top 
  const [globalErrorList, setGlobalErrorList] = useState([]);
  const tokenInputRef = useRef();


  // handle phone input field change
  const handlePhoneChange = (value) => {

    //console.log("print out phone change", value, value.length);

    // enable/disable "Send Code"
    if (value.length < 10) {
      setDisableSendCode(true);
    } else {
      setDisableSendCode(false);
    }

    setNewPhoneRequest({
      mobileNumber: value
    });
  }

  // handle six digit mfa token field change
  const handleMfaChange = (e) => {

    let value = e.target.value;
    const regex = /^[0-9\b]+$/;

    //console.log("test mfa code", regex.test(value));

    if (value === '' || regex.test(value)) {
      setMfaToken(e.target.value);
    }
  }

  // cancel back to the dashboard page
  const cancelMfa = () => {
    navigate("/dashboard");
  }

  // api to send code with new phone number
  const handleSendCode = async () => {
    // clear error
    setGlobalErrorList([]);
    // disable the send code button to avoid double click renable it after 30s
    setDisableSendCode(true);

    const res = await setupUserSmsFactor(token, newPhoneRequest);

    //console.log("handleSendCode()", res);

    if (res && res.data.status !== 1) {
      setGlobalErrorList(res.data.globalErrorMessageList);
      setDisableSendCode(false);
    } else {
      setTimeout(() => {
        setDisableSendCode(false);
      }, 30000);
      setShowSendCodeMsg(true);
      setDisableMfaTokenField(false);
      setTimeout(() => {
        tokenInputRef.current.focus();
      }, 500);
    }
  }

  // api to verify the new phone device
  const verifyToken = async () => {
    // clear error
    setGlobalErrorList([]);
    setShowSendCodeMsg(false);

    const res = await submitMfa(token, mfaToken, userEmail);
    //console.log("verifyToken", res);
    if (res && res.data.status !== 1) {
      setGlobalErrorList(res.data.globalErrorMessageList);
    } else {
      // update phone last 4 digit
      setMfaUserInfo({
        mobileNumber: res.data.data?.smsFactorMobileLast4
      });
      setGlobalSuccessStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: "Success!",
          msg: res.data.message,
          showContactHelp: false
        },
        redirect: {
          redirect: true,
          redirectFirst: true,
          path: "/dashboard",
          data: {}
        }
      });
    }
  }

  // initial loading
  useEffect(() => {
    getMfaUserInfo(token)
      .then((res) => {
        if (res !== null && res !== undefined) {
          //console.log("getMfaUserInfo()", res);

          var statusCode = res.data.status;
          if (statusCode < 0) {
            statusCode = STATUSES.ERROR.code;
          }
          switch (statusCode) {
            case STATUSES.ERROR.code:
              if (res.data.globalErrorMessageList && res.data.globalErrorMessageList.length != 0) {
                setGlobalErrorStatus({
                  dispatch: dispatch,
                  popupData: {
                    visible: true,
                    title: ERROR_MESSAGES.GENERAL_ERROR.title,
                    msg: res.data.globalErrorMessageList,
                    showContactHelp: false
                  },
                  redirect: {
                    redirect: false,
                    redirectFirst: false,
                    path: "",
                    data: {}
                  }
                })
              };
              break;
            case STATUSES.SUCCESS.code:
              setMfaUserInfo(res.data.data);
              break;
          }
        }
      }).catch((error) => {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });

        return error.response;
      });

  }, []);

  return (
    <Row>
      <Col xl={11} lg={11} md={11} sm={12}>
        <Card className="p-4">
          <Card.Body className="text-start">
            <h1 id="mainContent" className="header1 text-start mb-3 blue bold h1-form-title">
              Two-Factor Authentication
            </h1>
            {/**mfaStatusCode */}
            <p>{
              mfaUserInfo !== null ?
                <>
                  Your Two-Factor Authentication is setup&nbsp;
                  {mfaUserInfo.mfaStatusCode && mfaUserInfo.mfaStatusCode === 1 ?
                    <span style={{ "color": "#ac0606" }}>incomplete &nbsp;</span> : null
                  }
                  for +1 &#40;xxx&#41; xxx-{mfaUserInfo?.mobileNumber}. To change it, follow the steps below.
                </> : <>To set up your authentication phone number, follow the steps below.</>}
            </p>
            {showSendCodeMsg ?
              <>
                <PageInfo className="mb-3" type="info" message={"A code has been sent to your registered cell phone. Please enter that code in the field below. You may send a new code after 30 seconds if needed."}></PageInfo>
                <br />
              </>
              : null}
            {globalErrorList.map((item, index) =>
              <>
                <PageInfo key={"errors_" + index} type="error" message={item}></PageInfo>
                <br />
              </>
            )}
            <Form >
              <Form.Group as={Row} className="mb-3" controlId="mfaPhone">
                <Form.Label column sm="5" className="mfaLabel">
                  1. Enter a ten digit phone number
                </Form.Label>
                <Col sm={3}>
                  <PhoneInput
                    defaultMask="(...) ...-...."
                    alwaysDefaultMask="true"
                    country={"us"}
                    autoFormat="true"
                    enableAreaCodes="true"
                    disableCountryCode="true"
                    disableDropdown="true"
                    disableSearchIcon="true"
                    disableCountryGuess="true"
                    buttonStyle={{ display: 'none' }}
                    inputClass={"form-control"}
                    placeholder={""}
                    inputProps={{
                      id: "mfaPhone"
                    }}
                    onChange={handlePhoneChange}
                  />
                </Col>
              </Form.Group>
              <Row className="mb-4">
                <Col sm="5" className="pt-2 mfaLabel">2. Click to send code to your registered mobile device</Col>
                <Col>
                  <TextButton
                    className="mt-1"
                    disabled={disableSendCode}
                    variant="normal"
                    onClick={handleSendCode}
                    content={"Send Code"}
                  >
                  </TextButton>
                </Col>
              </Row >
              <Form.Group as={Row} className="mb-3" controlId="mfaToken">
                <Form.Label column sm="5" className="mfaLabel">
                  3. Enter the six digit code
                </Form.Label>
                <Col sm={3}>
                  <Form.Control
                    ref={tokenInputRef}
                    disabled={disableMfaTokenField}
                    type="text"
                    maxlength={6}
                    value={mfaToken}
                    onChange={handleMfaChange}
                  />
                </Col>
              </Form.Group>
              <Row className="mb-3 pt-1">
                <Col sm="5" className="pt-2 mfaLabel">4. Click Verify to confirm</Col>
                <Col className="mt-1">
                  <TextButton
                    className="w-10"
                    variant="normal"
                    disabled={mfaToken.length !== 6}
                    onClick={verifyToken}
                    content={"Verify"}
                  >
                  </TextButton>
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <TextButton
                    className="w-10"
                    variant="outline"
                    onClick={cancelMfa}
                    content={"Cancel"}
                  >
                  </TextButton>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default SetupMfa;